import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";
import SidebarService from "../../services/sidebar/SidebarService";
import {
  setSidebarData,
  setModuleMenus,
  clearMenus,
  resetSidebarState,
  setModuleActive,   // 👈 nuevo
} from "../../store/slices/sidebar/sidebarSlice";

/**
 * 🎯 Hook para manejar toda la lógica del sidebar dinámico:
 * - Cargar perfil activo y módulos.
 * - Cargar menús por módulo (lazy load).
 * - Gestionar menús almacenados por módulo.
 * - Controlar si hay un módulo activo.
 */
export const useSidebar = () => {
  const dispatch: AppDispatch = useDispatch();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const { profile, categories, menus, isModuleActive } = useSelector(
    (state: RootState) => state.sidebar
  );

  // ===================================================
  // 🌐 CONSULTAS
  // ===================================================

  /** 🔹 Obtener perfil activo + módulos + menús (árbol completo, un request) */
  const fetchSidebarData = async () => {
    try {
      showLoadingIndicator();
      const response = await SidebarService.getFullSidebar();
      const { statusCode, data } = response;

      if (statusCode === 200 && data) {
        dispatch(setSidebarData(data));
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  /** 🔹 Obtener menús de un módulo específico (lazy load) */
  const fetchModuleMenus = async (moduleCode: string) => {
    try {
      showLoadingIndicator();

      const response = await SidebarService.getModuleMenus(moduleCode);
      const { statusCode, data } = response;

      if (statusCode === 200 && Array.isArray(data)) {
        // ✅ Guardar los menús bajo su módulo correspondiente
        dispatch(setModuleMenus({ moduleCode, menus: data }));

        // ✅ Activar el módulo actual
        dispatch(setModuleActive(true));
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  // ===================================================
  // 🧭 CONTROL DE ESTADO DEL MÓDULO
  // ===================================================

  /** ✅ Marcar un módulo como activo */
  const activateModule = () => dispatch(setModuleActive(true));

  /** 🚫 Desactivar módulo (por ejemplo, al salir del área principal) */
  const deactivateModule = () => dispatch(setModuleActive(false));

  // ===================================================
  // 🧹 UTILIDADES
  // ===================================================
  const clearModuleMenus = () => dispatch(clearMenus());
  const reset = () => dispatch(resetSidebarState());

  /** 🔹 Obtener los menús ya cargados de un módulo */
  const getModuleMenus = (moduleCode: string) => menus[moduleCode] || [];

  // ===================================================
  // 📦 Retorno del hook
  // ===================================================
  return {
    profile,
    categories,
    menus,
    isModuleActive, // 👈 nuevo estado
    getModuleMenus,
    fetchSidebarData,
    fetchModuleMenus,
    activateModule,   // 👈 nuevo
    deactivateModule, // 👈 nuevo
    clearModuleMenus,
    reset,
  };
};

export default useSidebar;
